import React from 'react';
import { Button, Icon } from './primitives.jsx';

// Contact email — placeholder until a real address is wired up. Centralised
// here so a single edit propagates across all three legal pages.
export const PRIVACY_CONTACT = 'privacy@sweepwc26.com';
export const LAST_UPDATED = '22 May 2026';

// ============================================================================
// Shared layout for the three legal pages — keeps typography consistent and
// gives every page a "Back home" CTA + "Last updated" stamp.
// ============================================================================

const LegalPage = ({ title, subtitle, children, onBack }) => (
  <section style={{
    minHeight: 'var(--h-full)', background: 'var(--paper)',
    padding: '120px 24px 80px',
  }}>
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <button
        onClick={onBack}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontFamily: 'var(--font-mono)', fontSize: 13,
          color: 'var(--blue)', letterSpacing: '0.04em',
          padding: 0, marginBottom: 24,
          // display:flex puts the button on its own line so the SWEEP:WC26
          // tag below doesn't collide with it. width:fit-content keeps the
          // hover/click target tight to the text itself.
          display: 'flex', alignItems: 'center', gap: 6, width: 'fit-content',
        }}
      >
        <Icon name="arrow-left" size={14} /> BACK TO HOME
      </button>

      <div style={{
        fontFamily: 'var(--font-display)', fontSize: 14,
        letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--blue)',
      }}>SWEEP:WC26 · LEGAL</div>

      <h1 style={{
        fontFamily: 'var(--font-display)',
        fontSize: 'clamp(48px, 6vw, 80px)',
        letterSpacing: '-0.01em', textTransform: 'uppercase', lineHeight: 0.95,
        margin: '16px 0 12px', color: 'var(--ink)',
      }}>
        {title}
      </h1>

      {subtitle && (
        <p style={{
          color: 'var(--ink-soft)', fontSize: 18, lineHeight: 1.5,
          margin: '0 0 32px', maxWidth: 620,
        }}>{subtitle}</p>
      )}

      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 12,
        color: 'var(--ink-mute)', letterSpacing: '0.06em',
        marginBottom: 40,
      }}>
        LAST UPDATED · {LAST_UPDATED.toUpperCase()}
      </div>

      <div style={{
        color: 'var(--ink)', fontSize: 16, lineHeight: 1.6,
      }}>
        {children}
      </div>
    </div>
  </section>
);

// Section heading + paragraph helpers — keep the legal copy readable without
// hand-styling every <h2> and <p>.
const Section = ({ heading, children }) => (
  <section style={{ marginBottom: 32 }}>
    <h2 style={{
      fontFamily: 'var(--font-display)', fontSize: 22,
      letterSpacing: '0.04em', textTransform: 'uppercase',
      color: 'var(--ink)', margin: '0 0 12px',
    }}>{heading}</h2>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {children}
    </div>
  </section>
);

const P = ({ children }) => <p style={{ margin: 0 }}>{children}</p>;

const Ul = ({ children }) => (
  <ul style={{ margin: 0, paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 6 }}>
    {children}
  </ul>
);

// ============================================================================
// Privacy Policy — UK GDPR / DPA 2018 framing, plain English, scoped to the
// data this app actually collects (no analytics, no ads, no third-party
// trackers means the policy can be honestly short).
// ============================================================================

export const PrivacyPolicy = ({ onBack }) => (
  <LegalPage
    title="Privacy Policy"
    subtitle="What data SWEEP:WC26 collects, why, and the rights you have over it under UK GDPR and the Data Protection Act 2018."
    onBack={onBack}
  >
    <Section heading="Who we are">
      <P>
        SWEEP:WC26 is a free, non-commercial tool for running World Cup 2026 office
        sweepstakes. The site operator is the data controller for any personal
        data processed here. Questions, requests, or complaints? Email{' '}
        <a href={`mailto:${PRIVACY_CONTACT}`}>{PRIVACY_CONTACT}</a>.
      </P>
    </Section>

    <Section heading="What we collect">
      <P>We only collect what's strictly needed to run a sweepstake:</P>
      <Ul>
        <li><strong>Pool admin email</strong> — provided when you create a pool, used to send the admin secret and to recover lost links.</li>
        <li><strong>Player names</strong> — free-text strings entered by participants when they draw a team. We treat these as participant-chosen identifiers, not verified identities.</li>
        <li><strong>Team assignments</strong> — which name drew which country, and in which round.</li>
        <li><strong>Server access logs</strong> — IP address, user-agent, request path and timestamp. Retained for up to 30 days for security and debugging, then rotated out.</li>
      </Ul>
      <P>
        We do <strong>not</strong> use analytics, advertising trackers, fingerprinting,
        or any third-party marketing tools.
      </P>
    </Section>

    <Section heading="Why we collect it (lawful basis)">
      <Ul>
        <li><strong>Pool admin email</strong> — your <em>consent</em>, given when you submit the create-a-pool form. Withdraw at any time by emailing us to delete the pool.</li>
        <li><strong>Player names + team assignments</strong> — <em>legitimate interests</em> in providing the sweepstake feature you've signed up for. These can't be removed without effectively deleting the pool.</li>
        <li><strong>Server logs</strong> — <em>legitimate interests</em> in keeping the service secure and operational.</li>
      </Ul>
    </Section>

    <Section heading="How long we keep it">
      <P>
        Pool data (admin email, player names, team assignments) is retained for as
        long as the pool exists. When a pool is deleted — by you or by us — all
        associated data is permanently removed within 7 days, including from
        encrypted off-site backups in the next rotation cycle.
      </P>
      <P>
        Server access logs are retained for up to 30 days, then automatically rotated.
      </P>
    </Section>

    <Section heading="Who we share it with">
      <P>
        Nobody, by default. If the site operator has configured an SMTP email
        provider (e.g. Mailgun, SendGrid) for sending welcome and recovery emails,
        that provider acts as a processor and receives only the email content needed
        to deliver the message. We never sell or trade personal data.
      </P>
      <P>
        If you ever spot a security issue, error reports (sanitised of personal
        data) may be sent to Sentry for triage — but only if the site operator has
        opted into Sentry. Check with whoever deployed your instance.
      </P>
    </Section>

    <Section heading="Your rights">
      <P>Under UK GDPR you have the right to:</P>
      <Ul>
        <li><strong>Access</strong> the personal data we hold about you.</li>
        <li><strong>Rectify</strong> inaccurate data (e.g. fix a misspelled name).</li>
        <li><strong>Erase</strong> your data — for pool admins, this means deleting the pool.</li>
        <li><strong>Restrict</strong> or <strong>object to</strong> processing.</li>
        <li><strong>Withdraw consent</strong> at any time.</li>
        <li><strong>Lodge a complaint</strong> with the UK Information Commissioner's Office (<a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>).</li>
      </Ul>
      <P>
        To exercise any of these rights, email <a href={`mailto:${PRIVACY_CONTACT}`}>{PRIVACY_CONTACT}</a>{' '}
        with the pool ID. We aim to respond within 14 days.
      </P>
    </Section>

    <Section heading="Cookies and local storage">
      <P>
        SWEEP:WC26 does not set any cookies. We do use your browser's
        localStorage to remember which pools you've joined or created on this
        device. See the <a href="/cookies">Cookie Notice</a> for the full breakdown
        and how to clear it.
      </P>
    </Section>

    <Section heading="Changes to this policy">
      <P>
        Material changes will be reflected here with a new "last updated" date.
        We won't email you about minor wording tweaks, but significant changes
        to data handling will trigger a notice when you next open the site.
      </P>
    </Section>
  </LegalPage>
);

// ============================================================================
// Terms of Service — explicit "no real money, no prize" framing keeps this
// well clear of UK Gambling Commission territory.
// ============================================================================

export const TermsOfService = ({ onBack }) => (
  <LegalPage
    title="Terms of Service"
    subtitle="The rules of using SWEEP:WC26. Short, plain English, no surprises."
    onBack={onBack}
  >
    <Section heading="1. What this is">
      <P>
        SWEEP:WC26 is a free, non-commercial tool for running random-team
        sweepstakes for the FIFA World Cup 2026. By using the site, you accept
        these terms. If you don't, please don't use the site.
      </P>
    </Section>

    <Section heading="2. No real money, no prizes from us">
      <P>
        SWEEP:WC26 itself does not collect entry fees, hold money, or distribute
        prizes. The site is for fun and bragging rights only. If your office or
        group of friends decides to put real money on the line, that's entirely
        your private arrangement — we are not a party to it, we don't see it, and
        we accept no responsibility for it. We also can't help with disputes
        about who owes who a tenner.
      </P>
      <P>
        If you do run a paid pool, the UK Gambling Commission's rules around
        small-scale "work lotteries" and "private society lotteries" may apply.
        That's on you to comply with.
      </P>
    </Section>

    <Section heading="3. Acceptable use">
      <P>You agree not to:</P>
      <Ul>
        <li>Use the service for anything illegal, harassing, or harmful.</li>
        <li>Enter offensive, defamatory, or deliberately misleading names.</li>
        <li>Attempt to disrupt, reverse-engineer, or attack the service.</li>
        <li>Use automated tools to mass-create pools or draw teams.</li>
      </Ul>
      <P>
        Pool admins are responsible for the names and content within their pools.
        If a participant is uncomfortable being included, the admin should remove
        them. If you spot abuse, email <a href={`mailto:${PRIVACY_CONTACT}`}>{PRIVACY_CONTACT}</a>.
      </P>
    </Section>

    <Section heading="4. Accuracy and availability">
      <P>
        We do our best to keep the site running and the tournament data accurate,
        but everything here is provided <strong>"as is" with no warranty</strong>.
        Bracket states, group standings, and match results are entered manually
        by pool admins (or simulated for testing) and may not reflect real-world
        FIFA results. Don't use SWEEP:WC26 as a source of truth for anything that
        matters.
      </P>
    </Section>

    <Section heading="5. Limitation of liability">
      <P>
        To the fullest extent allowed by law, the site operator is not liable for
        any indirect, incidental, consequential, or special damages arising from
        your use of the service. Nothing in these terms limits liability for
        death, personal injury caused by negligence, or fraud — your statutory
        rights remain unaffected.
      </P>
    </Section>

    <Section heading="6. Termination">
      <P>
        We may suspend or delete pools, accounts, or the entire service at any
        time, with or without notice, if you breach these terms or if we need to
        for legal, technical, or operational reasons. We'll try to give reasonable
        notice where we can.
      </P>
    </Section>

    <Section heading="7. Governing law">
      <P>
        These terms are governed by the laws of England and Wales. Any disputes
        will be resolved in the English courts.
      </P>
    </Section>

    <Section heading="8. Contact">
      <P>
        Questions about these terms? Email{' '}
        <a href={`mailto:${PRIVACY_CONTACT}`}>{PRIVACY_CONTACT}</a>.
      </P>
    </Section>
  </LegalPage>
);

// ============================================================================
// Cookie Notice — honest, short, and accurate. We don't set cookies; we use
// localStorage. The distinction matters legally because no consent banner is
// required for strictly-functional first-party storage.
// ============================================================================

export const CookieNotice = ({ onBack }) => (
  <LegalPage
    title="Cookie Notice"
    subtitle="The short version: SWEEP:WC26 doesn't set any cookies. Here's what we do use, and how to clear it."
    onBack={onBack}
  >
    <Section heading="No cookies">
      <P>
        SWEEP:WC26 is a single-page application that doesn't set, read, or share
        any HTTP cookies — not first-party, not third-party, not analytics, not
        tracking. There is no cookie banner because there are no cookies to
        consent to.
      </P>
    </Section>

    <Section heading="What we DO use: localStorage">
      <P>
        To remember which pools you've created or joined on this device, we store
        a few small keys in your browser's <strong>localStorage</strong>.
        localStorage is first-party only — never sent to our servers, never
        accessible to other sites, and stays on your device until you clear it.
      </P>
      <Ul>
        <li>
          <code style={{ fontFamily: 'var(--font-mono)', fontSize: 13, background: 'var(--paper-warm)', padding: '1px 6px', borderRadius: 4 }}>
            sweep-wc26-secret-&lt;poolId&gt;
          </code>
          {' '}— your admin secret for a pool you created. Required to manage that pool.
        </li>
        <li>
          <code style={{ fontFamily: 'var(--font-mono)', fontSize: 13, background: 'var(--paper-warm)', padding: '1px 6px', borderRadius: 4 }}>
            sweep-wc26-drawn-&lt;poolId&gt;
          </code>
          {' '}— the team ID you drew in a pool you joined. Used to greet you with "your team" when you return.
        </li>
      </Ul>
    </Section>

    <Section heading="How to clear it">
      <P>You can wipe all SWEEP:WC26 data from your browser at any time:</P>
      <Ul>
        <li><strong>Chrome / Edge:</strong> Settings → Privacy and security → Clear browsing data → Cookies and other site data, scoped to this site.</li>
        <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data → Manage Data → search "sweepwc26".</li>
        <li><strong>Safari:</strong> Settings → Privacy → Manage Website Data → search and remove.</li>
      </Ul>
      <P>
        Clearing localStorage will sign you out of every pool you've touched on
        this device. You can recover pool access by entering your admin email at
        the "Lost your link?" prompt on the home page.
      </P>
    </Section>

    <Section heading="Third-party requests">
      <P>
        Your browser loads team flag images from a third-party CDN
        (<a href="https://www.jsdelivr.com" target="_blank" rel="noopener noreferrer">jsDelivr</a>),
        which serves static SVG files. jsDelivr does not set cookies for these
        requests, but the request itself reveals your IP address to them per
        standard HTTP behaviour. No other third-party requests are made.
      </P>
    </Section>

    <Section heading="Changes">
      <P>
        If we ever start setting cookies (we don't currently plan to), we'll
        update this notice and add a consent banner before the first cookie is
        set. Any change will be reflected by a new "last updated" date.
      </P>
    </Section>
  </LegalPage>
);

// ============================================================================
// Footer — small triple-link strip + copyright line, designed to live below
// every page's <main> content.
// ============================================================================

export const Footer = ({ onNav }) => (
  <footer style={{
    padding: '32px 24px',
    background: 'var(--paper-warm)',
    borderTop: '1px solid var(--hairline)',
    marginTop: 0,
  }}>
    <div style={{
      maxWidth: 1280, margin: '0 auto',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      gap: 16, flexWrap: 'wrap',
    }}>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 12,
        color: 'var(--ink-mute)', letterSpacing: '0.06em',
      }}>
        © 2026 SWEEP:WC26 · A free office sweepstake tool · Not affiliated with FIFA
      </span>

      <nav style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        {/* SSR-only guide — real navigation, not a SPA screen. */}
        <a
          href="/how-to-run-a-world-cup-sweepstake"
          style={{
            fontFamily: 'var(--font-mono)', fontSize: 12,
            letterSpacing: '0.06em', color: 'var(--ink-soft)',
            textDecoration: 'none',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--blue)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ink-soft)'; }}
        >
          GUIDE
        </a>
        {[
          ['/privacy', 'PRIVACY'],
          ['/terms',   'TERMS'],
          ['/cookies', 'COOKIES'],
        ].map(([path, label]) => (
          <a
            key={path}
            href={path}
            onClick={(e) => { e.preventDefault(); onNav(path); }}
            style={{
              fontFamily: 'var(--font-mono)', fontSize: 12,
              letterSpacing: '0.06em', color: 'var(--ink-soft)',
              cursor: 'pointer', textDecoration: 'none',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--blue)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ink-soft)'; }}
          >
            {label}
          </a>
        ))}
      </nav>
    </div>
  </footer>
);
